import { ColumnDef } from '@/components/product/ProductTable';
import { cn } from '@/lib/utils';
import { useQueryFilter } from '@/stores/useQueryFilterStore';
import { ColumnId, SortOption } from '@/types';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { memo } from 'react';

interface TableHeaderProps {
    activeColumns: ColumnDef[];
    theadId: string;
}

const SORT_FIELD_MAP: Record<string, string> = {
    name: 'name',
    brewery: 'brewery',
    untappd: 'untappd',
    style: 'style',
    packaging_type: 'packaging',
    volume: 'volume',
    price: 'price',
};

const SortIcon = memo(
    ({ columnId, sort }: { columnId: ColumnId; sort: string }) => {
        const field = SORT_FIELD_MAP[columnId];
        if (!field) return null;

        if (sort === `${field}`)
            return (
                <ArrowUp className="animate-in fade-in ml-1 h-3 w-3 text-brand-gold" />
            );

        if (sort === `-${field}`)
            return (
                <ArrowDown className="animate-in fade-in ml-1 h-3 w-3 text-brand-gold" />
            );

        return (
            <ArrowUpDown className="ml-1 h-3 w-3 text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100" />
        );
    },
);

const TableHeader = ({ theadId, activeColumns }: TableHeaderProps) => {
    const { sort, handleSort } = useQueryFilter();

    const handleHeaderClick = (columnId: ColumnId) => {
        let newSort: string = columnId;

        if (sort === columnId) {
            newSort = columnId[0] === '-' ? columnId.slice(1) : '-' + columnId;
        }

        handleSort(newSort as SortOption);
    };

    return (
        <thead
            id={theadId}
            className="border-b border-zinc-800 bg-zinc-900/80 text-[11px] font-bold tracking-widest text-zinc-500 uppercase"
        >
            <tr>
                {activeColumns.map((col) => (
                    <th
                        key={col.id}
                        className={cn(
                            'px-2 py-4 select-none',
                            col.sortable
                                ? 'group cursor-pointer transition-colors hover:text-zinc-300'
                                : '',
                            col.className,
                            col.headerClassName,
                        )}
                        onClick={
                            col.sortable
                                ? () => handleHeaderClick(col.id)
                                : () => {}
                        }
                    >
                        <div className="flex items-center">
                            <span
                                className={
                                    col.sortable
                                        ? 'underline underline-offset-2'
                                        : ''
                                }
                            >
                                {col.label}
                            </span>
                            {col.sortable && (
                                <SortIcon columnId={col.id} sort={sort} />
                            )}
                        </div>
                    </th>
                ))}
            </tr>
        </thead>
    );
};

export default TableHeader;
